/**
 * Centralized theme configuration - Single Source of Truth
 * This is the ONLY place where design tokens should be defined
 */

export const theme = {
  // Color palette - HSL values (Forest Green Theme, Light Mode Only)
  colors: {
    background: "40 20% 99%",           // Warm Off-White (Creamy)
    foreground: "164 33% 10%",         // Deep Dark Green/Black for text
    card: "40 20% 99%",                 // Warm Off-White (Creamy)
    cardForeground: "164 33% 10%",      // Deep Dark Green/Black
    popover: "40 20% 99%",              // Warm Off-White (Creamy)
    popoverForeground: "164 33% 10%",   // Deep Dark Green/Black
    primary: "165 37% 22%",             // Deep Forest Green Button
    primaryForeground: "0 0% 100%",     // White
    secondary: "164 30% 96%",           // Light Mint/Sage
    secondaryForeground: "165 37% 22%", // Deep Forest Green
    muted: "164 20% 94%",               // Grayish-Green
    mutedForeground: "164 10% 40%",     // Medium Gray-Green
    accent: "164 30% 94%",              // Light Mint/Sage (hover states)
    accentForeground: "165 37% 22%",    // Deep Forest Green
    destructive: "0 84% 60%",           // Standard Red
    destructiveForeground: "0 0% 98%",  // Off white
    border: "164 20% 90%",              // Subtle Gray-Green
    input: "164 20% 90%",               // Subtle Gray-Green
    ring: "165 37% 22%",                // Deep Forest Green
    // Status colors for scores and feedback
    success: "142 76% 36%",             // Green for high scores
    successForeground: "142 76% 96%",   // Light green background
    successText: "142 76% 20%",         // Dark green text
    warning: "38 92% 50%",              // Amber/Yellow for medium scores
    warningForeground: "38 92% 96%",    // Light amber background
    warningText: "38 92% 20%",          // Dark amber text
    error: "0 84% 60%",                 // Red for low scores
    errorForeground: "0 84% 96%",       // Light red background
    errorText: "0 84% 20%",             // Dark red text
  },

  // Spacing scale (rem-based for accessibility)
  spacing: {
    xs: "0.25rem",    // 4px
    sm: "0.5rem",     // 8px
    md: "1rem",       // 16px
    lg: "1.5rem",     // 24px
    xl: "2rem",       // 32px
    "2xl": "3rem",    // 48px
    "3xl": "4rem",    // 64px
    "4xl": "6rem",    // 96px
  },

  // Typography scale
  typography: {
    fontSize: {
      xs: "0.75rem",      // 12px
      sm: "0.875rem",     // 14px
      base: "1rem",       // 16px
      lg: "1.125rem",     // 18px
      xl: "1.25rem",      // 20px
      "2xl": "1.5rem",    // 24px
      "3xl": "1.875rem",  // 30px
      "4xl": "2.25rem",   // 36px
      "5xl": "3rem",      // 48px
    },
    lineHeight: {
      tight: "1.25",
      normal: "1.5",
      relaxed: "1.75",
    },
    fontWeight: {
      normal: "400",
      medium: "500",
      semibold: "600",
      bold: "700",
      extrabold: "800",
    },
  },

  // Border radius
  radius: {
    none: "0",
    sm: "0.25rem",     // 4px
    DEFAULT: "0.35rem", // 5.6px - base radius
    md: "0.5rem",      // 8px
    lg: "0.75rem",     // 12px
    xl: "1rem",        // 16px
    "2xl": "1.5rem",   // 24px
    full: "9999px",
  },

  // Box shadows
  shadows: {
    xs: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
    sm: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
    md: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
    lg: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
    xl: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
    "2xl": "0 25px 50px -12px rgb(0 0 0 / 0.25)",
    inner: "inset 0 2px 4px 0 rgb(0 0 0 / 0.05)",
    none: "0 0 #0000",
  },

  // Transitions
  transitions: {
    fast: "150ms",
    base: "200ms",
    slow: "300ms",
    slower: "500ms",
  },

  // Z-index scale
  zIndex: {
    base: "0",
    dropdown: "1000",
    sticky: "1020",
    fixed: "1030",
    modalBackdrop: "1040",
    modal: "1050",
    popover: "1060",
    tooltip: "1070",
  },
}

/**
 * Get CSS variable string for a color
 * @param {string} color - Color value in HSL format (e.g., "0 0% 100%")
 * @returns {string} CSS hsl() value
 */
export function getColorVar(color) {
  return `hsl(${color})`
}

/**
 * Get a color value from the theme
 * @param {string} colorKey - Key from theme.colors (e.g., "primary", "background")
 * @returns {string} CSS hsl() value
 */
export function getColor(colorKey) {
  const color = theme.colors[colorKey]
  return color ? getColorVar(color) : undefined
}

/**
 * Get a spacing value from the theme
 * @param {string} key - Key from theme.spacing (e.g., "md", "xl")
 * @returns {string} CSS rem value
 */
export function getSpacing(key) {
  return theme.spacing[key]
}

/**
 * Generate complete CSS with all theme variables
 * @returns {string} CSS string with all custom properties
 */
export function generateThemeCSS() {
  const { colors, spacing, typography, radius, shadows, transitions, zIndex } = theme
  
  return `
    :root {
      /* Colors */
      --background: ${colors.background};
      --foreground: ${colors.foreground};
      --card: ${colors.card};
      --card-foreground: ${colors.cardForeground};
      --popover: ${colors.popover};
      --popover-foreground: ${colors.popoverForeground};
      --primary: ${colors.primary};
      --primary-foreground: ${colors.primaryForeground};
      --secondary: ${colors.secondary};
      --secondary-foreground: ${colors.secondaryForeground};
      --muted: ${colors.muted};
      --muted-foreground: ${colors.mutedForeground};
      --accent: ${colors.accent};
      --accent-foreground: ${colors.accentForeground};
      --destructive: ${colors.destructive};
      --destructive-foreground: ${colors.destructiveForeground};
      --border: ${colors.border};
      --input: ${colors.input};
      --ring: ${colors.ring};
      --success: ${colors.success};
      --success-foreground: ${colors.successForeground};
      --success-text: ${colors.successText};
      --warning: ${colors.warning};
      --warning-foreground: ${colors.warningForeground};
      --warning-text: ${colors.warningText};
      --error: ${colors.error};
      --error-foreground: ${colors.errorForeground};
      --error-text: ${colors.errorText};

      /* Spacing */
      --spacing-xs: ${spacing.xs};
      --spacing-sm: ${spacing.sm};
      --spacing-md: ${spacing.md};
      --spacing-lg: ${spacing.lg};
      --spacing-xl: ${spacing.xl};
      --spacing-2xl: ${spacing["2xl"]};
      --spacing-3xl: ${spacing["3xl"]};
      --spacing-4xl: ${spacing["4xl"]};

      /* Typography */
      --text-xs: ${typography.fontSize.xs};
      --text-sm: ${typography.fontSize.sm};
      --text-base: ${typography.fontSize.base};
      --text-lg: ${typography.fontSize.lg};
      --text-xl: ${typography.fontSize.xl};
      --text-2xl: ${typography.fontSize["2xl"]};
      --text-3xl: ${typography.fontSize["3xl"]};
      --text-4xl: ${typography.fontSize["4xl"]};
      --text-5xl: ${typography.fontSize["5xl"]};

      --leading-tight: ${typography.lineHeight.tight};
      --leading-normal: ${typography.lineHeight.normal};
      --leading-relaxed: ${typography.lineHeight.relaxed};

      --font-normal: ${typography.fontWeight.normal};
      --font-medium: ${typography.fontWeight.medium};
      --font-semibold: ${typography.fontWeight.semibold};
      --font-bold: ${typography.fontWeight.bold};
      --font-extrabold: ${typography.fontWeight.extrabold};

      /* Border Radius */
      --radius: ${radius.DEFAULT};
      --radius-none: ${radius.none};
      --radius-sm: ${radius.sm};
      --radius-md: ${radius.md};
      --radius-lg: ${radius.lg};
      --radius-xl: ${radius.xl};
      --radius-2xl: ${radius["2xl"]};
      --radius-full: ${radius.full};

      /* Shadows */
      --shadow-xs: ${shadows.xs};
      --shadow-sm: ${shadows.sm};
      --shadow-md: ${shadows.md};
      --shadow-lg: ${shadows.lg};
      --shadow-xl: ${shadows.xl};
      --shadow-2xl: ${shadows["2xl"]};
      --shadow-inner: ${shadows.inner};

      /* Transitions */
      --transition-fast: ${transitions.fast};
      --transition-base: ${transitions.base};
      --transition-slow: ${transitions.slow};
      --transition-slower: ${transitions.slower};

      /* Z-Index */
      --z-base: ${zIndex.base};
      --z-dropdown: ${zIndex.dropdown};
      --z-sticky: ${zIndex.sticky};
      --z-fixed: ${zIndex.fixed};
      --z-modal-backdrop: ${zIndex.modalBackdrop};
      --z-modal: ${zIndex.modal};
      --z-popover: ${zIndex.popover};
      --z-tooltip: ${zIndex.tooltip};
    }
  `
}

/**
 * Get the complete theme object
 * @returns {object} Theme configuration
 */
export function getTheme() {
  return theme
}

// Export individual pieces for convenience
export const colors = theme.colors
export const spacing = theme.spacing
export const typography = theme.typography
export const radius = theme.radius
export const shadows = theme.shadows
export const transitions = theme.transitions
export const zIndex = theme.zIndex
